import { defineStore } from 'pinia';
//@ts-ignore
import { calculateMonthlyPaymentForVehicle } from "@cf247/pricing/src/monthly-payment-calculator"
import { VehicleService } from '../services';
import { LoanApplication, Vehicle } from '../types/loan-application';
import { useAuthStore } from './auth-store';
import { useProposalStore } from './proposal-store';
import {
  useMockHpProposal, useFutures, useMockPrimeProposals
} from '../functions';
import { useSettingsStore } from './settings-store';

export const useVehicleStore = defineStore('vehicle', {
  state: () => ({
    vehicles: [] as Vehicle[],
    stockUpdatedAt: null as (null | Date),
    selectedVehicle: null as (null | number),
    loading: false
  }),
  actions: {
    setVehicles(vehicles: Vehicle[]) {
      this.vehicles = vehicles;
    },
    resetStockUpdate() {
      this.stockUpdatedAt = null;
    },
    getStockVehicles() {
      
      return new Promise((resolve, reject) => {
        if (!this.vehicles || this.vehicles.length === 0) {
          this.loading = true;
        }
        if (useAuthStore().selectedDealers) {
          if (useAuthStore().selectedDealers!.length <= 20) {
            let vs: Vehicle[] = [];
            let tasks: Promise<Vehicle[]>[] = [];
            useAuthStore().selectedDealers!.forEach(d => {
              tasks.push(
                new Promise<Vehicle[]>((resolve, reject) => {
                  VehicleService.getStockVehicles(d.dealerId!).then(res => {
                    vs = vs.concat(res.vehicles);
                    resolve(res.vehicles)
                  }).catch((error) => {
                    resolve([]);
                  })
                })
              )
            })
            Promise.all(tasks).then(() => {
              this.vehicles = vs.sort((a, b) => b.price - a.price);
              this.stockUpdatedAt = new Date();
              this.loading = false;
              resolve(vs);
            })
          }
          else {
            this.loading = false;
            reject("TOO_MANY_DEALERS")
          }
        }
        else {
          VehicleService.getStockVehicles(useAuthStore().dealerId!).then(res => {
            this.vehicles = res.vehicles//.filter(v => v.vehicleId === 21461259);
            this.stockUpdatedAt = new Date();
            this.loading = false;
            resolve(res.vehicles);
          })
            .catch((error) => {
              this.loading = false;
              console.log(error)
              reject(error);
            })
        }
      })
    },
    // Price stock using customer proposals, if no proposals passed, use mock prime
    getPricedStock(app: LoanApplication, score?: number) {
      return new Promise<Vehicle[]>(async (resolve, reject) => {

        this.vehicles.forEach(v => {
          if (score !== undefined && score >= 0) {
            let props = app.proposals;
            
            switch (score) {
              case 2:
                props = useMockPrimeProposals(v.price);
                const lowestBespokeRate = useProposalStore().bespokeRates && useProposalStore().bespokeRates!.length > 0 ? useProposalStore().bespokeRates!.reduce((a, b) => a.rate < b.rate ? a : b).rate : null; 
                props.forEach(p => {
                  p.apr = lowestBespokeRate ?? 9.9;
                })
                break;
              case 1:
                props = [useMockHpProposal(v.price, 13.9)]
                props[0].maxMileage = 120000;
                break;
              case 0:
                props = [useMockHpProposal(v.price, 19.9)]
                props[0].maxMileage = 120000;
                props[0].maxLendAmount = 100000;
                break;
            }

            app.proposals = props;
          }

          v.annualMileage = app.vehicle && app.vehicle.annualMileage ? app.vehicle.annualMileage : useProposalStore().defaultPcpMileage
         
          calculateMonthlyPaymentForVehicle(
            v,
            app.proposals,
            app.deposit ?? 0,
            app.pxValue ?? 0,
            null,
            {
              annualMileage: v.annualMileage,
              pxSettlement: app.settlement ?? 0,
              terms: [24, 36, 48, 60].concat(useSettingsStore().termsToShow.filter(t => ![24, 36, 48, 60].includes(t))),
              bespokeRates: useProposalStore().bespokeRates,
              ignorePcpLender:[49],
              ignoreLTV: true,
            }
          )

          v.proposals = app.proposals;
        })

        resolve(this.vehicles);
      })

    },
    getPricedStockSingle(app: LoanApplication, vehicle: Vehicle, score?: number) {
      return new Promise<Vehicle>(async (resolve, reject) => {

        if (score !== undefined && score >= 0) {
          let props = app.proposals;

          switch (score) {
            case 2:
              props = useMockPrimeProposals(vehicle.price);
              break;
            case 1:
              props = [useMockHpProposal(vehicle.price, 19.9)]
              props[0].maxMileage = 120000;
              break;
            case 0:
              props = [useMockHpProposal(vehicle.price, 29.9)]
              props[0].maxMileage = 120000;
              props[0].maxLendAmount = 100000;
              break;
          }

          app.proposals = props;
        }

        app.vehicle = vehicle;

        let futures = await useFutures(app);

        vehicle.annualMileage = app.vehicle.annualMileage ?? useProposalStore().defaultPcpMileage

        calculateMonthlyPaymentForVehicle(
          vehicle,
          app.proposals,
          app.deposit ?? 0,
          app.pxValue ?? 0,
          null,
          {
            annualMileage: app.vehicle.annualMileage ?? useProposalStore().defaultPcpMileage,
            bespokeRates: useProposalStore().bespokeRates,
            pxSettlement: app.settlement ?? 0,
            terms: [24, 36, 48, 60].concat(useSettingsStore().termsToShow.filter(t => ![24, 36, 48, 60].includes(t))),
            ignorePcpLender:[49],
            ignoreLTV: true,
          }
        )

        vehicle.proposals = app.proposals;

        if (vehicle.futuresRequestFailed) {
          vehicle.pcpMatrix = [];
          vehicle.cannotPricePCP = true;
          vehicle.cannotPriceReasonPCP = "No residuals"
        }

        app.vehicle = vehicle;

        const i = this.vehicles.findIndex(v => v.vehicleId! === vehicle.vehicleId!);
        this.vehicles[i] = app.vehicle;

        resolve(app.vehicle);
      })

    },
    setSelectedVehicle(id: number | null) {
      this.selectedVehicle = id;
    }
  },
  persist: true
});
